import type { TicketRecoveryReason, UpdateTicketData, User } from '@field-tickets/shared';
import { showToast } from '@/components/Toast';
import { pruneExpiredTicketStorage, writeTicketStorage } from '@/utils/ticketStorageRetention';
import {
  describeUnsavedTicketEdits,
  type TicketConflictSnapshot,
} from '@/utils/offlineQueue';

/**
 * The durable recovery drafts of a ticket (#967).
 *
 * Written when a ticket write ends in recovery (an uncertain or committed
 * answer, or a 409 that cannot be merged), from whichever path: a save, a
 * status change, a QuickBooks send or an offline replay. A draft holds the
 * user's latest local edits and the copyable summary of them. It is not part
 * of the replay queue and nothing ever sends it: the replay skips any ticket
 * that has one, and the only way out is a confirmed network read the user
 * chose (useTicketRecovery), which moves every draft of the ticket into its
 * retained drafts (ticketRetainedDrafts.ts, #1041) and clears them here.
 *
 * Each draft belongs to one signed-in user and one browser tab:
 * `ft-ticket-recovery:<user>:<ticket>:<tab>`. A tab writes only its own key,
 * so a second tab entering recovery never overwrites the first tab's edits;
 * the recovery panel shows every draft of the ticket. Drafts can hold
 * financial values, so a draft is only ever read back for its own user, and
 * drafts stay hidden with an empty session or a different account signed in
 * (`syncTicketRecoveryOwner`, wired in the auth store).
 *
 * Stored synchronously in localStorage, so a draft survives closing or hiding
 * the ticket and a frozen-then-discarded mobile tab; an in-memory copy covers
 * browsers where localStorage refuses the write, and then the user is told
 * the edits last only until the tab closes (never a silent loss).
 *
 * Retention: every owner's drafts expire 30 days after their last write.
 */
export interface TicketRecoveryDraft {
  owner: string;
  tabId: string;
  ticketId: string;
  reason: TicketRecoveryReason;
  message: string;
  /** Fields the server changed since the draft's base, when known. */
  changedFields: string[];
  /** The user's latest local edits (pinned to their version token). */
  payload: UpdateTicketData;
  baseSnapshot?: TicketConflictSnapshot;
  /** The payload the form held for the base version, when a form wrote it (#1041). */
  basePayload?: Record<string, unknown>;
  /** `payload` as copyable text. */
  summary: string;
  createdAt: number;
}

/** Fired on window when a draft is written or cleared in this tab. */
export const TICKET_RECOVERY_EVENT = 'ft:ticket-recovery';
const STORAGE_PREFIX = 'ft-ticket-recovery:';
const memoryDrafts = new Map<string, TicketRecoveryDraft>();
let memoryOwner: string | null = null;
/** Keys whose draft localStorage refused: kept in memory only. */
const memoryOnlyKeys = new Set<string>();

export const TICKET_RECOVERY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const TICKET_RECOVERY_STORAGE_WARNING =
  "This browser couldn't store your unsaved ticket edits. They are kept only until this tab closes: copy them before closing it.";

function newTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // Fall through.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** This tab's id: one per page load, so tabs never share a draft key. */
export const TICKET_RECOVERY_TAB_ID = newTabId();

/** The draft owner for a user: the auth UUID, never an email or Trello id. */
export function ticketRecoveryOwner(user: User | null | undefined): string | null {
  return user?.memberUuid || user?.id || null;
}

function ownerPrefix(owner: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(owner)}:`;
}

function ticketPrefix(owner: string, ticketId: string): string {
  return `${ownerPrefix(owner)}${encodeURIComponent(ticketId)}:`;
}

export function ticketRecoveryStorageKey(owner: string, ticketId: string, tabId = TICKET_RECOVERY_TAB_ID): string {
  return `${ticketPrefix(owner, ticketId)}${encodeURIComponent(tabId)}`;
}

/** True when a storage key belongs to this owner's drafts of this ticket. */
export function isTicketRecoveryStorageKey(key: string | null, owner: string, ticketId: string): boolean {
  return Boolean(key && key.startsWith(ticketPrefix(owner, ticketId)));
}

export function ticketRecoveryMessage(reason: TicketRecoveryReason): string {
  if (reason === 'uncertain') {
    return "We couldn't confirm your last change. It may have saved. Reload the ticket and check it.";
  }
  if (reason === 'committed') {
    return 'Your last change went through, but the ticket could not be refreshed. Reload the ticket and check it.';
  }
  return 'This ticket changed elsewhere while you were editing. Reload the ticket to continue.';
}

export function buildTicketRecoveryDraft(
  owner: string,
  ticketId: string,
  reason: TicketRecoveryReason,
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
  changedFields: string[] = [],
  basePayload?: Record<string, unknown>,
): TicketRecoveryDraft {
  return {
    owner,
    tabId: TICKET_RECOVERY_TAB_ID,
    ticketId,
    reason,
    message: ticketRecoveryMessage(reason),
    changedFields,
    payload,
    baseSnapshot,
    basePayload,
    summary: describeUnsavedTicketEdits(payload, baseSnapshot),
    createdAt: Date.now(),
  };
}

function notify(ticketId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TICKET_RECOVERY_EVENT, { detail: { ticketId } }));
}

function storageKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
  } catch {
    // No storage.
  }
  return keys;
}

function removeDraftKey(key: string): void {
  memoryDrafts.delete(key);
  memoryOnlyKeys.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing more to remove.
  }
}

function readStoredDraft(key: string): TicketRecoveryDraft | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null') as TicketRecoveryDraft | null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function isExpired(draft: Pick<TicketRecoveryDraft, 'createdAt'>, now: number): boolean {
  return !(typeof draft.createdAt === 'number' && now - draft.createdAt < TICKET_RECOVERY_MAX_AGE_MS);
}

/** Drops only expired drafts; creating one never discards another unsent edit. */
function enforceRetention(): void {
  pruneExpiredTicketStorage();
  const now = Date.now();
  const byKey = new Map<string, number>();
  for (const [key, draft] of memoryDrafts) byKey.set(key, draft.createdAt);
  for (const key of storageKeys()) {
    if (byKey.has(key)) continue;
    byKey.set(key, readStoredDraft(key)?.createdAt ?? Number.NaN);
  }
  for (const [key, createdAt] of byKey) {
    if (Number.isFinite(createdAt) && isExpired({ createdAt }, now)) removeDraftKey(key);
  }
}

/**
 * Writes this tab's draft (`draft.tabId` is always this tab). When
 * localStorage refuses it the draft is kept in memory and the user is warned
 * that it lasts only until the tab closes.
 */
export function writeTicketRecoveryDraft(draft: TicketRecoveryDraft, entryIdentity?: string): boolean {
  enforceRetention();
  const own = { ...draft, tabId: entryIdentity ? `queue:${entryIdentity}` : TICKET_RECOVERY_TAB_ID };
  const key = ticketRecoveryStorageKey(own.owner, own.ticketId, own.tabId);
  memoryDrafts.set(key, own);
  try {
    writeTicketStorage(key, JSON.stringify(own));
    memoryOnlyKeys.delete(key);
  } catch {
    // Storage refused (private mode, quota): the in-memory copy still keeps
    // the recovery state for this page session. Say so.
    memoryOnlyKeys.add(key);
    showToast(TICKET_RECOVERY_STORAGE_WARNING, 'warning');
  }
  notify(own.ticketId);
  return !memoryOnlyKeys.has(key);
}

/** Only the active owner may disclose memory-only recovery edits. */
export function memoryOnlyTicketRecoveryText(owner: string | null): string {
  enforceRetention();
  if (!owner || owner !== memoryOwner) return '';
  return [...memoryOnlyKeys].map((key) => memoryDrafts.get(key))
    .filter((draft) => draft?.owner === owner)
    .map((draft) => `Ticket ${draft!.ticketId}\n${draft!.summary}`).join('\n\n');
}

/** True when a draft of this ticket could only be kept in memory. */
export function ticketRecoveryDraftsMemoryOnly(owner: string | null, ticketId: string): boolean {
  if (!owner) return false;
  const prefix = ticketPrefix(owner, ticketId);
  return [...memoryOnlyKeys].some((key) => key.startsWith(prefix));
}

function isDraft(value: unknown, owner: string, ticketId: string): value is TicketRecoveryDraft {
  const draft = value as Partial<TicketRecoveryDraft> | null;
  return Boolean(
    draft &&
    draft.owner === owner &&
    draft.ticketId === ticketId &&
    typeof draft.tabId === 'string' &&
    typeof draft.reason === 'string' &&
    typeof draft.summary === 'string' &&
    draft.payload &&
    typeof draft.payload === 'object',
  );
}

/** Every draft of this ticket for this user, oldest first. */
export function listTicketRecoveryDrafts(owner: string | null, ticketId: string): TicketRecoveryDraft[] {
  if (!owner || !ticketId) return [];
  const prefix = ticketPrefix(owner, ticketId);
  const now = Date.now();
  const byKey = new Map<string, TicketRecoveryDraft>();
  for (const [key, draft] of memoryDrafts) {
    if (key.startsWith(prefix) && isDraft(draft, owner, ticketId)) byKey.set(key, draft);
  }
  for (const key of storageKeys()) {
    if (!key.startsWith(prefix) || byKey.has(key)) continue;
    const parsed = readStoredDraft(key);
    // The owner inside the draft must match too: a key alone is not trusted.
    if (isDraft(parsed, owner, ticketId)) byKey.set(key, parsed);
  }
  for (const [key, draft] of byKey) {
    if (isExpired(draft, now)) {
      removeDraftKey(key);
      byKey.delete(key);
    }
  }
  return [...byKey.values()].sort((a, b) => a.createdAt - b.createdAt);
}

/** This tab's draft, else the newest one (the edits a reopened form shows). */
export function readTicketRecoveryDraft(owner: string | null, ticketId: string): TicketRecoveryDraft | null {
  const drafts = listTicketRecoveryDrafts(owner, ticketId);
  return drafts.find((d) => d.tabId === TICKET_RECOVERY_TAB_ID) ?? drafts.at(-1) ?? null;
}

/**
 * For the offline replay: does any draft of this ticket exist, whoever wrote
 * it; production callers pass the captured owner so another account cannot
 * block this account with its retained drafts.
 */
export function anyTicketRecoveryDraft(ticketId: string, owner: string | null = memoryOwner): boolean {
  const marker = `:${encodeURIComponent(ticketId)}:`;
  const now = Date.now();
  for (const key of new Set([...memoryDrafts.keys(), ...storageKeys()])) {
    if (!key.includes(marker) || (owner && !key.startsWith(ownerPrefix(owner)))) continue;
    const draft = memoryDrafts.get(key) ?? readStoredDraft(key);
    // An unreadable entry still holds the save back; an expired one goes.
    if (draft && isExpired(draft, now)) {
      removeDraftKey(key);
      continue;
    }
    return true;
  }
  return false;
}

/** True when a stored (not memory-only) draft of this ticket holds exactly `payload`. */
export function ticketRecoveryDraftHoldsDurably(owner: string | null, ticketId: string, payload: UpdateTicketData): boolean {
  if (!owner) return false;
  const prefix = ticketPrefix(owner, ticketId);
  const wanted = JSON.stringify(payload);
  return storageKeys().some((key) => {
    if (!key.startsWith(prefix) || memoryOnlyKeys.has(key)) return false;
    const stored = readStoredDraft(key);
    return isDraft(stored, owner, ticketId) && JSON.stringify(stored.payload) === wanted;
  });
}

/** Clears every tab's draft of this ticket (after a reload the user chose). */
export function clearTicketRecoveryDrafts(owner: string | null, ticketId: string): void {
  if (!owner) return;
  const prefix = ticketPrefix(owner, ticketId);
  for (const key of [...memoryDrafts.keys(), ...storageKeys()]) if (key.startsWith(prefix)) removeDraftKey(key);
  notify(ticketId);
}

/**
 * Entry from a write with no form state to hand (the offline replay, the
 * close-time flush) that ended in recovery. Writes this tab's draft unless it
 * already has one (that one holds the same or newer edits); an open form of
 * the ticket hears the event and enters recovery.
 */
export function recordTicketRecovery(
  owner: string | null,
  ticketId: string,
  reason: TicketRecoveryReason,
  payload: UpdateTicketData,
  baseSnapshot: TicketConflictSnapshot | undefined,
  entryIdentity?: string,
): void {
  if (!owner) return;
  const tabId = entryIdentity ? `queue:${entryIdentity}` : TICKET_RECOVERY_TAB_ID;
  if (listTicketRecoveryDrafts(owner, ticketId).some((d) => d.tabId === tabId)) return;
  writeTicketRecoveryDraft(buildTicketRecoveryDraft(owner, ticketId, reason, payload, baseSnapshot), entryIdentity);
}

/** Explicit/test cleanup only; authentication never calls this. */
export function purgeTicketRecoveryDrafts(): void {
  memoryOwner = null;
  memoryDrafts.clear();
  memoryOnlyKeys.clear();
  for (const key of storageKeys()) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing more to clear.
    }
  }
  try {
    localStorage.removeItem('ft-ticket-recovery-owner');
  } catch {
    // No storage.
  }
}

/** Owner-keyed drafts survive account changes; reads remain scoped by their owner. */
export function syncTicketRecoveryOwner(owner: string | null): void {
  enforceRetention();
  memoryOwner = owner;
}

/** The signed-in owner, for disclosing memory-only edits (retained drafts too). */
export function activeTicketRecoveryOwner(): string | null {
  return memoryOwner;
}

/** The close/leave warning while a ticket needs a reload. */
export const RECOVERY_CLOSE_WARNING =
  'This ticket needs a reload before it can be edited or saved. Your unsaved edits stay on this device and come back when you reopen it. Close anyway?';

export function __resetTicketRecoveryForTest(): void {
  purgeTicketRecoveryDrafts();
}
